// components/SaveFilterPresetModal.jsx
import React, { useState } from "react";
import { X, Save, AlertCircle } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const SaveFilterPresetModal = ({ isOpen, onClose, onSave }) => {
  const { isDarkMode } = useTheme();
  const [presetName, setPresetName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [scope, setScope] = useState("personal");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!presetName.trim()) {
      setError("Preset name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave({
        name: presetName.trim(),
        description: description.trim(),
        isDefault,
        isPublic,
        scope: isPublic ? scope : "personal",
      });

      // Reset form
      setPresetName("");
      setDescription("");
      setIsDefault(false);
      setIsPublic(false);
      setScope("personal");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save filter preset");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
      <div
        className={`w-full max-w-md rounded-lg shadow-xl ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-base font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Save Filter Preset
          </h3>
          <button
            onClick={onClose}
            className={`p-1 transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div
              className={`flex items-start gap-2 p-3 rounded-lg ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <AlertCircle
                size={16}
                className={`mt-0.5 flex-shrink-0 ${
                  isDarkMode ? "text-red-400" : "text-red-500"
                }`}
              />
              <p
                className={`text-sm ${isDarkMode ? "text-red-400" : "text-red-600"}`}
              >
                {error}
              </p>
            </div>
          )}

          {/* Preset Name */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Preset Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g., Hot Leads - Tech Industry"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
              }`}
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this filter is for..."
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
              }`}
              maxLength={500}
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            {/* Set as Default */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className={`w-4 h-4 rounded focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-blue-600 focus:ring-offset-gray-800"
                    : "bg-white border-gray-300 text-blue-600 focus:ring-offset-white"
                }`}
              />
              <span
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Set as my default filter
              </span>
            </label>

            {/* Make Public/Shared */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className={`w-4 h-4 rounded focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-blue-600 focus:ring-offset-gray-800"
                    : "bg-white border-gray-300 text-blue-600 focus:ring-offset-white"
                }`}
              />
              <div className="flex-1">
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Share this filter
                </span>
                <p
                  className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  Make this filter available to your team or company
                </p>
              </div>
            </label>

            {/* Scope Selection (only shown when isPublic is checked) */}
            {isPublic && (
              <div className="ml-6 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scope"
                    value="team"
                    checked={scope === "team"}
                    onChange={(e) => setScope(e.target.value)}
                    className={`w-4 h-4 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-blue-600 focus:ring-offset-gray-800"
                        : "bg-white border-gray-300 text-blue-600 focus:ring-offset-white"
                    }`}
                  />
                  <div>
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Team
                    </span>
                    <p
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Share with your team
                    </p>
                  </div>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scope"
                    value="company"
                    checked={scope === "company"}
                    onChange={(e) => setScope(e.target.value)}
                    className={`w-4 h-4 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-blue-600 focus:ring-offset-gray-800"
                        : "bg-white border-gray-300 text-blue-600 focus:ring-offset-white"
                    }`}
                  />
                  <div>
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Company
                    </span>
                    <p
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Share with entire company
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 transition-colors ${
                isDarkMode
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-600"
                  : "bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-400"
              }`}
            >
              <Save size={16} />
              {isSubmitting ? "Saving..." : "Save Preset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveFilterPresetModal;
